import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLanguage } from "../i18n/LanguageContext";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { getFileUrl, renewSignedFileUrl } from "../utils/fileUrlHelper";
import SecurePdfViewer from "./SecurePdfViewer";
import "../styles/components/secure-media-viewer.css";

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "m4v", "mov", "webm"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "m4a", "aac", "wav", "ogg", "webm"]);

function extensionOf(value = "") {
  const clean = String(value || "").split(/[?#]/)[0].toLowerCase();
  const match = clean.match(/\.([a-z0-9]{1,8})$/);
  return match?.[1] || "";
}

function mediaKind(item) {
  const safeItem = item && typeof item === "object" ? item : {};
  const mime = String(safeItem.mimeType || safeItem.fileType || safeItem.dosyaTipi || "").toLowerCase();
  const ext = extensionOf(safeItem.fileName || safeItem.dosyaAdi || safeItem.name || safeItem.title || safeItem.url);
  const declaredType = String(safeItem.type || "").toLowerCase();
  if ((mime.startsWith("image/") && mime !== "image/svg+xml") || IMAGE_EXTENSIONS.has(mime) || IMAGE_EXTENSIONS.has(ext) || declaredType === "image") return "image";
  if (mime.startsWith("video/") || VIDEO_EXTENSIONS.has(mime) || VIDEO_EXTENSIONS.has(ext) || declaredType === "video") return "video";
  if (mime.startsWith("audio/") || AUDIO_EXTENSIONS.has(mime) || AUDIO_EXTENSIONS.has(ext) || declaredType === "audio") return "audio";
  if (["application/pdf", "pdf"].includes(mime) || ext === "pdf") return "pdf";
  return "unsupported";
}

function safeSource(value) {
  const resolved = getFileUrl(value);
  if (!resolved) return "";
  if (/^(blob:|data:image\/(?:jpeg|jpg|png|gif|webp);)/i.test(resolved)) return resolved;
  if (typeof window === "undefined") return "";
  try {
    const parsed = new URL(resolved, window.location.origin);
    if (parsed.protocol === "https:") return parsed.toString();
    if (parsed.protocol === "http:" && ["localhost", "127.0.0.1"].includes(parsed.hostname)) return parsed.toString();
  } catch (_) {}
  return "";
}

const touchDistance = (touches) => Math.hypot(
  touches[0].clientX - touches[1].clientX,
  touches[0].clientY - touches[1].clientY
);

export default function SecureMediaViewer({ open, item, onClose, lang: forcedLang }) {
  const language = useLanguage();
  const lang = forcedLang || language?.lang || "tr";
  const tr = lang !== "en";
  const [status, setStatus] = useState("idle");
  const [currentSource, setCurrentSource] = useState("");
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const viewportRef = useRef(null);
  const gestureRef = useRef(null);
  const renewalAttemptedRef = useRef(false);

  const sourceUrl = useMemo(() => safeSource(item?.url || item?.rawUrl || item?.dosyaUrl || ""), [item]);
  const name = String(item?.fileName || item?.dosyaAdi || item?.name || item?.title || (tr ? "Dosya önizleme" : "File preview"));
  const kind = mediaKind(item);
  const kindLabel = ({ image: tr ? "Fotoğraf" : "Photo", pdf: "PDF", video: tr ? "Video" : "Video", audio: tr ? "Ses" : "Audio" }[kind] || (tr ? "Dosya" : "File"));
  const mediaLoaded = useCallback(() => setStatus("ready"), []);
  const mediaFailed = useCallback(async () => {
    if (!renewalAttemptedRef.current && kind !== "pdf" && /\/files\/(?:view|preview|stream)\//.test(currentSource)) {
      renewalAttemptedRef.current = true;
      try {
        const renewed = await renewSignedFileUrl(currentSource);
        if (renewed && renewed !== currentSource) {
          setCurrentSource(renewed);
          setStatus("loading");
          return;
        }
      } catch (_) {}
    }
    setStatus("error");
  }, [currentSource, kind]);
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("secureMediaViewerOpen");
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("secureMediaViewerOpen");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setTransform({ scale: 1, x: 0, y: 0 });
    gestureRef.current = null;
    renewalAttemptedRef.current = false;
    setCurrentSource(sourceUrl);
    setStatus(sourceUrl ? (kind === "unsupported" ? "unsupported" : "loading") : "error");
  }, [kind, open, sourceUrl]);

  if (!open || !item || typeof document === "undefined") return null;

  const clampPan = (x, y, scale) => {
    const viewport = viewportRef.current;
    if (!viewport || scale <= 1) return { x: 0, y: 0 };
    const maxX = Math.max(0, ((viewport.clientWidth * scale) - viewport.clientWidth) / 2);
    const maxY = Math.max(0, ((viewport.clientHeight * scale) - viewport.clientHeight) / 2);
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  };

  const onTouchStart = (event) => {
    if (kind !== "image") return;
    if (event.touches.length === 2) {
      gestureRef.current = { mode: "pinch", distance: touchDistance(event.touches), ...transform };
    } else if (event.touches.length === 1 && transform.scale > 1) {
      gestureRef.current = { mode: "pan", startX: event.touches[0].clientX, startY: event.touches[0].clientY, ...transform };
    }
  };

  const onTouchMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || kind !== "image") return;
    event.preventDefault();
    if (gesture.mode === "pinch" && event.touches.length === 2) {
      const scale = Math.max(1, Math.min(4, gesture.scale * (touchDistance(event.touches) / Math.max(gesture.distance, 1))));
      setTransform({ scale, ...clampPan(gesture.x, gesture.y, scale) });
    } else if (gesture.mode === "pan" && event.touches.length === 1) {
      const pan = clampPan(gesture.x + event.touches[0].clientX - gesture.startX, gesture.y + event.touches[0].clientY - gesture.startY, transform.scale);
      setTransform((current) => ({ ...current, ...pan }));
    }
  };

  const onTouchEnd = (event) => {
    if (event.touches.length === 0) gestureRef.current = null;
    if (transform.scale <= 1.01) setTransform({ scale: 1, x: 0, y: 0 });
  };

  const onDoubleClick = () => {
    if (kind !== "image") return;
    setTransform((current) => current.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2, x: 0, y: 0 });
  };

  return ReactDOM.createPortal(
    <div
      className="secureMediaOverlay noSwipeBack"
      data-no-swipe-back="true"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
    >
      <section className="secureMediaShell" onClick={(event) => event.stopPropagation()}>
        <header className="secureMediaHeader">
          <div className="secureMediaTitleGroup">
            <span className={`secureMediaKindIcon is-${kind}`} aria-hidden="true">
              {kind === "image" ? "◇" : kind === "pdf" ? "PDF" : kind === "video" ? "▶" : kind === "audio" ? "♫" : "＋"}
            </span>
            <div><strong title={name}>{name}</strong><small>{kindLabel}</small></div>
          </div>
          <button type="button" className="secureMediaClose" onClick={onClose} aria-label={tr ? "Kapat" : "Close"}>×</button>
        </header>

        <main className={`secureMediaStage secureMediaStage--${kind}`}>
          {status === "loading" && <div className="secureMediaState secureMediaLoading" role="status"><span className="secureMediaSpinner" /><strong>{tr ? "Dosya hazırlanıyor" : "Preparing file"}</strong></div>}
          {status === "error" && <div className="secureMediaState secureMediaState--error"><span>!</span><strong>{tr ? "Önizleme açılamadı" : "Preview could not be opened"}</strong><small>{tr ? "Dosya kaldırılmış veya erişim süresi dolmuş olabilir." : "The file may have been removed or its access may have expired."}</small></div>}
          {status === "unsupported" && <div className="secureMediaState"><span>📎</span><strong>{tr ? "Bu dosya türü önizlenemiyor" : "This file type cannot be previewed"}</strong><small>{tr ? "Dosyayı önceki ekrandaki İndir düğmesiyle açabilirsiniz." : "You can open it with the Download button on the previous screen."}</small></div>}
          {kind === "image" && currentSource && (
            <div ref={viewportRef} className={`secureMediaImageViewport ${status === "ready" ? "isReady" : ""}`} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd} onDoubleClick={onDoubleClick}>
              <img src={currentSource} alt={name} draggable="false" onLoad={mediaLoaded} onError={mediaFailed} style={{ transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})` }} />
            </div>
          )}
          {kind === "pdf" && currentSource && <SecurePdfViewer src={currentSource} title={name} lang={lang} onReady={mediaLoaded} onError={mediaFailed} />}
          {kind === "video" && currentSource && <video className={`secureMediaVideo ${status === "ready" ? "isReady" : ""}`} src={currentSource} controls playsInline preload="metadata" onLoadedMetadata={mediaLoaded} onError={mediaFailed} />}
          {kind === "audio" && currentSource && <div className={`secureMediaAudio ${status === "ready" ? "isReady" : ""}`}><span aria-hidden="true">🎧</span><strong>{name}</strong><audio src={currentSource} controls preload="metadata" onLoadedMetadata={mediaLoaded} onError={mediaFailed} /></div>}
        </main>
        {(kind === "image" || kind === "pdf") && status === "ready" && (
          <footer className="secureMediaFooter">
            <span className="secureMediaReadyDot" aria-hidden="true" />
            <span>{kind === "image" ? (tr ? "Yakınlaştırmak için çift dokunun veya iki parmak kullanın" : "Double-tap or pinch to zoom") : (tr ? "Belge içinde kaydırabilir ve yakınlaştırabilirsiniz" : "Scroll and zoom within the document")}</span>
            {kind === "image" && <b>%{Math.round(transform.scale * 100)}</b>}
          </footer>
        )}
      </section>
    </div>,
    document.body
  );
}
